import { useCallback, useEffect, useState } from 'react'
import { PageHeader } from '@/shell/AppShell'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchJobErrors,
  fetchServices,
  fetchMigrations,
  INFRA_STR as S,
  type JobRunRow,
  type ServiceHealth,
  type MigrationRow,
} from './infraApi'

/**
 * A17 live monitoring — the three infra screens that map to REAL, deployed
 * MyGram endpoints (no fabricated data):
 *   A17.10 Xato loglari      → GET /admin/job-runs?status=error (errored job runs)
 *   A17.14 DB holati         → GET /admin/infra/services (postgres ping+latency)
 *   A17.23 Migratsiya holati → GET /admin/devtools/migrations (schema inventory)
 * Isolated file: no edits to InfraDetailScreens.tsx / HOT files. Helpers below
 * mirror InfraDetailScreens' tiny load-hook (kept local to avoid cross-imports).
 */

/** Abortable fetch + reload; each screen uses this. */
function useLoad<T>(fn: (s: AbortSignal) => Promise<T>) {
  const [data, setData] = useState<T | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fn(ac.signal)
      .then((d) => setData(d))
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])
  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { data, err, reload }
}

function RefreshBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
    >
      {S.refresh}
    </button>
  )
}

function Rows({ n = 8 }: { n?: number }) {
  return (
    <div className="space-y-1.5 p-4">
      {Array.from({ length: n }).map((_, i) => (
        <Skeleton key={i} className="h-7 w-full" />
      ))}
    </div>
  )
}

function fmtMs(ms: number): string {
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(1)} s`
}

/* ── A17.10 — Xato loglari (errored background-job runs) ────────── */
export function InfraErrorLogsScreen() {
  const { data, err, reload } = useLoad<JobRunRow[]>(fetchJobErrors)
  return (
    <>
      <PageHeader title={S.errorLogs} screenId="A17.10" description={S.errorLogsDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows />
        ) : data.length === 0 ? (
          <EmptyState title={S.noErrors} />
        ) : (
          <div className="mx-auto max-w-4xl overflow-hidden rounded-lg border border-border-default">
            <table className="w-full text-left text-sm">
              <thead className="bg-surface-2 text-xs text-tertiary">
                <tr>
                  <th className="px-4 py-2 font-medium">{S.job}</th>
                  <th className="px-4 py-2 font-medium">{S.detail}</th>
                  <th className="px-4 py-2 text-right font-medium">{S.duration}</th>
                  <th className="px-4 py-2 text-right font-medium">{S.when}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((r) => (
                  <tr key={r.id} className="border-t border-border-subtle align-top">
                    <td className="px-4 py-2 font-mono text-danger">{r.job_name}</td>
                    <td className="max-w-md px-4 py-2 text-secondary">{r.detail || '—'}</td>
                    <td className="px-4 py-2 text-right tabular text-tertiary">{fmtMs(r.duration_ms)}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-tertiary">
                      {new Date(r.ran_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A17.14 — DB holati va replikatsiya ────────────────────────── */
export function InfraDbScreen() {
  const { data, err, reload } = useLoad<ServiceHealth[]>(fetchServices)
  const pg = data?.find((s) => s.name.toLowerCase().includes('postgres'))
  return (
    <>
      <PageHeader title={S.db} screenId="A17.14" description={S.dbDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows n={3} />
        ) : (
          <div className="mx-auto max-w-3xl space-y-3">
            {/* PostgreSQL health */}
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-primary">{S.dbHealth}</span>
                <span className={`flex items-center gap-1 text-xs ${pg?.up ? 'text-success' : 'text-danger'}`}>
                  <span aria-hidden>●</span>
                  {pg?.up ? S.up : S.down}
                </span>
              </div>
              <p className="mt-3 text-2xl font-semibold text-primary tabular">
                {pg ? pg.latency_ms.toFixed(1) : '—'}
                <span className="ml-1 text-sm font-normal text-tertiary">ms</span>
              </p>
              <p className="mt-1 truncate text-xs text-tertiary">{pg?.detail || '—'}</p>
            </div>
            {/* Replication — honest single-node note (no replica endpoint) */}
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <p className="text-sm font-medium text-primary">{S.replication}</p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-tertiary">{S.primary}</p>
                  <p className="mt-0.5 text-sm text-primary">postgres</p>
                </div>
                <div>
                  <p className="text-xs text-tertiary">{S.replica}</p>
                  <p className="mt-0.5 text-sm text-secondary">{S.none}</p>
                </div>
              </div>
              <p className="mt-3 text-xs text-tertiary">{S.singleNode}</p>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A17.23 — Migratsiya holati (DB) ───────────────────────────── */
export function InfraMigrationsScreen() {
  const { data, err, reload } = useLoad<MigrationRow[]>(fetchMigrations)
  const appliedN = data?.filter((m) => m.applied).length ?? 0
  const pendingN = data ? data.length - appliedN : 0
  return (
    <>
      <PageHeader title={S.migrations} screenId="A17.23" description={S.migrationsDesc} actions={<RefreshBtn onClick={reload} />} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !data ? (
          <Rows />
        ) : data.length === 0 ? (
          <EmptyState title={S.noMigrations} />
        ) : (
          <div className="mx-auto max-w-3xl space-y-3">
            <div className="grid grid-cols-2 gap-2.5">
              <div className="rounded-lg border border-border-default bg-surface-2 p-3">
                <p className="text-xs text-tertiary">{S.appliedCount}</p>
                <p className="mt-0.5 text-2xl font-semibold text-success tabular">{appliedN}</p>
              </div>
              <div className="rounded-lg border border-border-default bg-surface-2 p-3">
                <p className="text-xs text-tertiary">{S.pendingCount}</p>
                <p className={`mt-0.5 text-2xl font-semibold tabular ${pendingN > 0 ? 'text-warning' : 'text-tertiary'}`}>
                  {pendingN}
                </p>
              </div>
            </div>
            <div className="overflow-hidden rounded-lg border border-border-default">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs text-tertiary">
                  <tr>
                    <th className="px-4 py-2 font-medium">{S.version}</th>
                    <th className="px-4 py-2 font-medium">{S.migration}</th>
                    <th className="px-4 py-2 text-right font-medium">{S.applied}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((m) => (
                    <tr key={m.version} className="border-t border-border-subtle">
                      <td className="px-4 py-2 font-mono text-secondary">{m.version}</td>
                      <td className="px-4 py-2 text-primary">{m.name || '—'}</td>
                      <td className="px-4 py-2 text-right">
                        <span
                          className={`rounded px-1.5 py-0.5 text-xs ${
                            m.applied ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                          }`}
                        >
                          {m.applied ? S.applied : S.pendingMig}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  )
}
